
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Procurement } from '@/types/database.types';
import { useState, useMemo } from 'react';
import { extractRequestorInfo } from '@/services/procurement/userDataService';

interface OptimizedWarehouseParams {
  status?: 'pending' | 'confirmed' | 'rejected';
  pageSize?: number;
}

export const useOptimizedWarehouseProcurements = ({
  status = 'pending',
  pageSize = 25
}: OptimizedWarehouseParams = {}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: result,
    isLoading,
    error
  } = useQuery({
    queryKey: ['optimized-warehouse-procurements', status, currentPage, pageSize, searchTerm],
    queryFn: async () => {
      console.log(`[OptimizedWarehouse] Fetching ${status} procurements - Page ${currentPage}`);
      
      // Build different queries based on status to use proper joins
      let query;
      
      if (status === 'confirmed') {
        // Use !inner join to only get procurements WITH confirmed warehouse records
        query = supabase
          .from('procurements')
          .select(`
            *,
            requestor:user_profiles!fk_procurements_requestor(
              id, first_name, last_name, role, created_at, updated_at, status
            ),
            costs:procurement_costs(*),
            finance:procurement_finance(*),
            warehouse:procurement_warehouse!inner(*),
            items:procurement_items(*)
          `, { count: 'exact' })
          .not('warehouse.warehouse_confirmed_by', 'is', null)
          .is('warehouse.warehouse_rejected_by', null) // Ensure not also rejected
          .order('created_at', { ascending: false });
      } else if (status === 'rejected') {
        // Use !inner join to only get procurements WITH rejected warehouse records
        query = supabase
          .from('procurements')
          .select(`
            *,
            requestor:user_profiles!fk_procurements_requestor(
              id, first_name, last_name, role, created_at, updated_at, status
            ),
            costs:procurement_costs(*),
            finance:procurement_finance(*),
            warehouse:procurement_warehouse!inner(*),
            items:procurement_items(*)
          `, { count: 'exact' })
          .not('warehouse.warehouse_rejected_by', 'is', null)
          .is('warehouse.warehouse_confirmed_by', null) // Ensure not also confirmed
          .order('created_at', { ascending: false });
      } else {
        // Pending: standard query with left join
        query = supabase
          .from('procurements')
          .select(`
            *,
            requestor:user_profiles!fk_procurements_requestor(
              id, first_name, last_name, role, created_at, updated_at, status
            ),
            costs:procurement_costs(*),
            finance:procurement_finance(*),
            warehouse:procurement_warehouse(*),
            items:procurement_items(*)
          `, { count: 'exact' })
          .eq('status', 'pending_warehouse')
          .order('created_at', { ascending: false });
      }

      // Apply search filter if provided
      if (searchTerm) {
        query = query.or(`product_name.ilike.%${searchTerm}%,notes.ilike.%${searchTerm}%`);
      }

      // Calculate pagination
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;
      
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error(`Error fetching ${status} procurements:`, error);
        throw error;
      }

      console.log(`[OptimizedWarehouse] Fetched ${data?.length || 0} ${status} procurements, total count: ${count}`);

      // Batch fetch source names for better performance
      const sourceNames = await fetchSourceNamesInBatch(data || []);
      
      // Enrich data with source names and fix requestor typing
      const enrichedData = (data || []).map(procurement => ({
        ...procurement,
        source_name: sourceNames[procurement.source_id] || null,
        // Fix requestor typing - extract from array if it exists
        requestor: extractRequestorInfo(procurement.requestor),
        // Calculate total quantity from items if not set
        total_quantity: procurement.total_quantity || 
          procurement.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0
      }));

      return {
        data: enrichedData as Procurement[],
        count: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    },
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Helper function to batch fetch source names
  const fetchSourceNamesInBatch = async (procurements: any[]): Promise<Record<string, string | null>> => {
    const sourceMap: Record<string, string | null> = {};
    
    // Group by source type
    const groupedSources = procurements.reduce<Record<string, string[]>>((acc, proc) => {
      if (!proc.source_id || !proc.source_type) return acc;
      
      if (!acc[proc.source_type]) acc[proc.source_type] = [];
      acc[proc.source_type].push(proc.source_id);
      return acc;
    }, {});

    // Batch fetch for each source type
    const promises = Object.entries(groupedSources).map(async ([sourceType, ids]) => {
      try {
        switch (sourceType) {
          case 'farm': {
            const { data } = await supabase
              .from('farms')
              .select('id, farmer_name')
              .in('id', ids);
            
            data?.forEach(item => sourceMap[item.id] = item.farmer_name);
            break;
          }
          
          case 'supplier': {
            const { data } = await supabase
              .from('suppliers')
              .select('id, name')
              .in('id', ids);
            
            data?.forEach(item => sourceMap[item.id] = item.name);
            break;
          }
          
          case 'agent': {
            const { data } = await supabase
              .from('agents')
              .select('id, name')
              .in('id', ids);
            
            data?.forEach(item => sourceMap[item.id] = item.name);
            break;
          }
        }
      } catch (error) {
        console.error(`Error fetching ${sourceType} names:`, error);
      }
    });

    await Promise.all(promises);
    return sourceMap;
  };

  const procurements = result?.data || [];
  const totalItems = result?.count || 0;
  const totalPages = result?.totalPages || 1;

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  return {
    procurements,
    isLoading,
    error,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    searchTerm,
    setSearchTerm,
    goToPage,
    hasNextPage,
    hasPreviousPage
  };
};
